import { useState } from "react";
import { LayoutDashboard, Eye, Users, AlertTriangle, GitMerge, Table2, Workflow, Sparkles, FlaskConical, ShieldCheck, MessageSquare, Milestone, PanelLeftClose, PanelLeftOpen, ChevronLeft, XCircle, CalendarDays, ListChecks, Rocket, Package } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const productNavItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "overview", label: "Overview", icon: Eye },
  { id: "stakeholders", label: "Stakeholders", icon: Users },
  { id: "painpoints", label: "Pain Points", icon: AlertTriangle },
  { id: "tobe-enhancements", label: "Requirements", icon: Sparkles },
  { id: "tracking", label: "Tracking", icon: ListChecks },
  { id: "milestones", label: "Milestones", icon: Milestone },
  { id: "calendar", label: "My Tasks", icon: CalendarDays },
  { id: "feedback", label: "Communications", icon: MessageSquare },
  { id: "asis-flow", label: "As-Is Experience Model", icon: GitMerge },
  { id: "tobe-flow", label: "To-Be Experience Model", icon: Workflow },
  { id: "tobe-mapping", label: "To-Be Field Mapping", icon: Table2 },
  { id: "uat", label: "UAT", icon: FlaskConical },
  { id: "gonogo", label: "Go / No-Go", icon: ShieldCheck },
  { id: "gtm", label: "Go-to-Market", icon: Rocket },
];

export const Sidebar = ({ activePanel, onNavigate, collapsed, onToggle, selectedProduct, isAdmin, user, mobileOpen, onMobileClose }) => {
  const { selectProduct, isEmulating, emulatedBy, stopEmulating } = useAuth();

  const handleBackToProducts = () => {
    selectProduct(null);
    onNavigate('myproducts');
  };

  return (
    <aside className={`fixed left-0 top-0 h-full bg-[#0F172A] text-white flex flex-col border-r border-[#1E293B] transition-all duration-300 overflow-hidden
      ${collapsed ? "w-16" : "w-64"}
      ${mobileOpen ? "z-50 translate-x-0" : "-translate-x-full md:translate-x-0 z-50"}
    `} data-testid="sidebar">
      {/* Emulation Banner */}
      {isEmulating && !collapsed && (
        <div className="bg-amber-500 px-3 py-2" data-testid="emulation-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-amber-900" />
              <span className="text-xs font-semibold text-amber-900">Viewing as {user?.name}</span>
            </div>
            <button 
              onClick={stopEmulating}
              className="text-amber-900 hover:text-amber-950 transition-colors"
              title="Stop emulating"
              data-testid="stop-emulating-btn"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {isEmulating && collapsed && (
        <div className="bg-amber-500 px-2 py-2 flex justify-center" data-testid="emulation-banner">
          <button 
            onClick={stopEmulating}
            className="text-amber-900 hover:text-amber-950 transition-colors"
            title="Stop emulating"
            data-testid="stop-emulating-btn"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Current Product at Top - Only when product is selected */}
      {selectedProduct ? (
        <div className={`pt-4 pb-3 border-b border-[#1E293B] ${collapsed ? "px-2" : "px-3"}`}>
          {!collapsed ? (
            <button 
              onClick={handleBackToProducts}
              className="w-full flex items-center gap-2 px-3 py-2.5 bg-[#00D4C8]/10 border border-[#00D4C8]/30 rounded-lg text-left hover:bg-[#00D4C8]/20 transition-colors"
              data-testid="current-product-badge"
            >
              <ChevronLeft className="w-4 h-4 text-[#00D4C8]" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#00D4C8] font-medium">Current Product</p>
                <p className="text-sm text-white font-semibold truncate">{selectedProduct.name}</p>
              </div>
            </button>
          ) : (
            <button 
              onClick={handleBackToProducts}
              className="w-full flex items-center justify-center p-2 bg-[#00D4C8]/10 border border-[#00D4C8]/30 rounded-lg hover:bg-[#00D4C8]/20 transition-colors"
              data-testid="current-product-badge"
              title={selectedProduct.name}
            >
              <ChevronLeft className="w-4 h-4 text-[#00D4C8]" />
            </button>
          )}
        </div>
      ) : (
        /* Minimal header when no product selected */
        <div className={`pt-5 pb-4 border-b border-[#1E293B] ${collapsed ? "px-3" : "px-5"}`}>
          {!collapsed && (
            <p className="text-xs text-slate-500 font-medium">Product Lifecycle Management</p>
          )}
        </div>
      )}

      {/* Collapse toggle */}
      <div className={`px-3 pt-3 ${collapsed ? "flex justify-center" : ""}`}>
        <button onClick={onToggle} data-testid="sidebar-toggle"
          className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/60 transition-all">
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      {/* Nav items */}
      <div className="flex-1 py-2 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <style>{`.sidebar-nav::-webkit-scrollbar { display: none; }`}</style>
        <nav className={`flex flex-col gap-0.5 ${collapsed ? "px-2" : "px-3"} sidebar-nav`} data-testid="sidebar-nav">
          {/* My Tasks - always visible */}
          {!selectedProduct && (
            <button onClick={() => onNavigate('calendar')} data-testid="nav-calendar" title={collapsed ? "My Tasks" : undefined}
              className={`flex items-center ${collapsed ? "justify-center" : "gap-3"} ${collapsed ? "px-0 py-2.5" : "px-3 py-2.5"} text-sm font-medium rounded-lg transition-all duration-200 w-full text-left
                ${activePanel === 'calendar' ? "bg-slate-800 text-white shadow-sm ring-1 ring-slate-700" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"}`}>
              <CalendarDays className="w-[18px] h-[18px] flex-shrink-0" />
              {!collapsed && <span className="truncate">My Tasks</span>}
            </button>
          )}

          {/* Product-specific nav items - Only when a product is selected */}
          {selectedProduct && productNavItems.map((item, idx) => {
            const Icon = item.icon;
            const isActive = activePanel === item.id;
            return (
              <button key={item.id} onClick={() => onNavigate(item.id)} data-testid={`nav-${item.id}`} title={collapsed ? item.label : undefined}
                style={{ animationDelay: `${idx * 30}ms` }}
                className={`flex items-center ${collapsed ? "justify-center" : "gap-3"} ${collapsed ? "px-0 py-2.5" : "px-3 py-2.5"} text-sm font-medium rounded-lg transition-all duration-200 w-full text-left group
                  ${isActive ? "bg-gradient-to-r from-slate-800 to-slate-800/80 text-white shadow-sm ring-1 ring-slate-700" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"}`}>
                <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-transform group-hover:scale-110 ${isActive ? "text-[#00D4C8]" : ""}`} />
                {!collapsed && <span className="truncate">{item.label}</span>}
                {isActive && !collapsed && <div className="w-1 h-1 rounded-full bg-[#00D4C8] ml-auto animate-pulse" />}
              </button>
            );
          })}

          {/* Show message when no product selected */}
          {!selectedProduct && !collapsed && (
            <div className="px-3 py-8 text-center">
              <Package className="w-10 h-10 text-slate-700 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Select a product from your profile menu to get started</p>
            </div>
          )}
        </nav>
      </div>

    </aside>
  );
};
